// AdminUserDrawer — full create/edit drawer wired to the real backend.
//
// BE (ISSUE-44) now supports:
//   - POST   /admin-users (create with optional roleNames)
//   - PUT    /admin-users/{id} (profile: email, firstName, lastName, phone, avatar)
//   - DELETE /admin-users/{id} (soft delete)
//   - POST   /admin-users/{id}/assign-role | revoke-role
//   - POST   /app-users/{id}/reset-password (reused for admins)

import { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Drawer,
  Form,
  Grid,
  Input,
  Space,
  Spin,
  Tag,
  Typography,
} from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { adminUsersApi } from '@api/adminUsers';
import { rolesApi } from '@api/roles';
import { resolveApiErrorMessage } from '@/i18n/apiError';
import { recordAuditAction } from '@features/audit/recordAction';
import { UserProjectsSection } from '@features/projects/components/UserProjectsSection';
import { isSuperAdmin } from '@shared/rbac/permissionMap';
import type { AdminUser } from '@features/auth/types';

const { Text } = Typography;
const { useBreakpoint } = Grid;

interface Props {
  open: boolean;
  mode: 'create' | 'edit';
  user?: AdminUser | null;
  onClose: () => void;
  onSaved: () => void;
}

interface ProfileFormValues {
  email: string;
  fullName: string;
  phoneNumber?: string;
  password?: string;
}

function generatePassword(): string {
  const lower = 'abcdefghijkmnpqrstuvwxyz';
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const digits = '23456789';
  const symbols = '!@#$%^&*';
  const all = lower + upper + digits + symbols;
  const rand = (s: string): string => s.charAt(Math.floor(Math.random() * s.length));
  let pwd = rand(lower) + rand(upper) + rand(digits) + rand(symbols);
  for (let i = 0; i < 8; i++) pwd += rand(all);
  return pwd
    .split('')
    .sort(() => Math.random() - 0.5)
    .join('');
}

/**
 * Inner drawer body — kept distinct so the outer wrapper can re-mount it with a
 * fresh `key` when the target user changes, sidestepping
 * `react-hooks/set-state-in-effect` (we initialise state directly from props
 * instead of syncing via effect).
 */
function AdminUserDrawerBody({ mode, user, onClose, onSaved }: Omit<Props, 'open'>) {
  const qc = useQueryClient();
  const isEdit = mode === 'edit' && !!user;
  const screens = useBreakpoint();
  const { t } = useTranslation(['users', 'common']);

  const initialValues: ProfileFormValues = {
    email: user?.email ?? '',
    fullName: user?.fullName ?? '',
    phoneNumber: '',
    password: '',
  };

  const [form] = Form.useForm<ProfileFormValues>();
  const [pending, setPending] = useState<Set<string>>(
    () => new Set(user?.roles ?? []),
  );
  const [resetPwd, setResetPwd] = useState<string | null>(null);

  const { data: roles = [], isFetching: rolesFetching } = useQuery({
    queryKey: ['roles', { includeSystem: true }],
    queryFn: () => rolesApi.list({ includeSystem: true }),
  });

  const dirtyRoles = useMemo(() => {
    if (!user) return pending.size > 0;
    const existing = new Set(user.roles ?? []);
    if (existing.size !== pending.size) return true;
    for (const r of existing) if (!pending.has(r)) return true;
    return false;
  }, [user, pending]);

  const createMutation = useMutation({
    mutationFn: async (values: ProfileFormValues) => {
      const password = values.password?.trim() || generatePassword();
      const created = await adminUsersApi.create({
        email: values.email.trim(),
        fullName: values.fullName.trim(),
        password,
        roleNames: Array.from(pending),
      });
      return { created, password };
    },
    onSuccess: async ({ created, password }) => {
      toast.success(t('drawer.toast.created', { name: created.fullName }));
      setResetPwd(password);
      await qc.invalidateQueries({ queryKey: ['admin-users'] });
      void recordAuditAction({
        action: 'adminUser.create',
        resource: 'adminUser',
        resourceId: created.id,
        summary: `${created.email} admin kullanıcısı oluşturuldu`,
        meta: { roles: Array.from(pending) },
      });
      onSaved();
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const updateProfileMutation = useMutation({
    mutationFn: async (values: ProfileFormValues) => {
      if (!user) throw new Error('no user');
      return adminUsersApi.update(user.id, {
        email: values.email.trim(),
        fullName: values.fullName.trim(),
        phoneNumber: values.phoneNumber?.trim() || undefined,
      });
    },
    onSuccess: async (updated) => {
      toast.success(t('drawer.toast.profileUpdated'));
      await qc.invalidateQueries({ queryKey: ['admin-users'] });
      void recordAuditAction({
        action: 'adminUser.update',
        resource: 'adminUser',
        resourceId: updated.id,
        summary: `${updated.email} profili güncellendi`,
      });
      onSaved();
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const saveRolesMutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error('no user');
      const existing = new Set(user.roles ?? []);
      const toAdd = Array.from(pending).filter((r) => !existing.has(r));
      const toRemove = Array.from(existing).filter((r) => !pending.has(r));
      for (const r of toAdd) await adminUsersApi.assignRole(user.id, r);
      for (const r of toRemove) await adminUsersApi.revokeRole(user.id, r);
      return { added: toAdd, removed: toRemove };
    },
    onSuccess: async ({ added, removed }) => {
      toast.success(t('drawer.toast.rolesUpdated'));
      await qc.invalidateQueries({ queryKey: ['admin-users'] });
      void recordAuditAction({
        action: 'adminUser.roles.update',
        resource: 'adminUser',
        resourceId: user?.id,
        summary: `${user?.email} kullanıcısının rolleri güncellendi`,
        meta: { added, removed },
      });
      onSaved();
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const resetPwdMutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error('no user');
      const pwd = generatePassword();
      await adminUsersApi.update(user.id, { resetPassword: pwd });
      return pwd;
    },
    onSuccess: (pwd) => {
      setResetPwd(pwd);
      void recordAuditAction({
        action: 'adminUser.resetPassword',
        resource: 'adminUser',
        resourceId: user?.id,
        summary: `${user?.email} kullanıcısının şifresi sıfırlandı`,
      });
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error('no user');
      await adminUsersApi.remove(user.id);
    },
    onSuccess: async () => {
      toast.success(t('drawer.toast.userDeleted'));
      await qc.invalidateQueries({ queryKey: ['admin-users'] });
      void recordAuditAction({
        action: 'adminUser.delete',
        resource: 'adminUser',
        resourceId: user?.id,
        summary: `${user?.email} admin kullanıcısı silindi`,
      });
      onSaved();
      onClose();
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const drawerWidth = screens.md ? 560 : '100%';

  return (
    <Drawer
      title={
        isEdit
          ? t('drawer.title.edit', { name: user?.fullName ?? '' })
          : t('drawer.title.create')
      }
      open
      onClose={onClose}
      width={drawerWidth}
      destroyOnClose
    >
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Form
          form={form}
          layout="vertical"
          initialValues={initialValues}
          onFinish={(values) => {
            if (isEdit) updateProfileMutation.mutate(values);
            else createMutation.mutate(values);
          }}
          autoComplete="off"
        >
          <Form.Item
            label={t('drawer.labels.email')}
            name="email"
            rules={[
              { required: true, message: t('drawer.validation.emailRequired') },
              { type: 'email', message: t('drawer.validation.email') },
            ]}
          >
            <Input
              placeholder={t('drawer.placeholders.email')}
              disabled={updateProfileMutation.isPending || createMutation.isPending}
            />
          </Form.Item>

          <Form.Item
            label={t('drawer.labels.fullName')}
            name="fullName"
            rules={[
              { required: true, message: t('drawer.validation.fullNameRequired') },
              { min: 2, max: 100, message: t('drawer.validation.fullNameLength') },
            ]}
          >
            <Input
              placeholder={t('drawer.placeholders.fullName')}
              disabled={updateProfileMutation.isPending || createMutation.isPending}
            />
          </Form.Item>

          <Form.Item label={t('drawer.labels.phone')} name="phoneNumber">
            <Input placeholder={t('drawer.placeholders.phone')} />
          </Form.Item>

          {!isEdit ? (
            <Form.Item
              label={t('drawer.labels.tempPassword')}
              name="password"
              extra={t('drawer.tempPasswordExtra')}
            >
              <Input.Password placeholder={t('drawer.placeholders.password')} />
            </Form.Item>
          ) : null}

          <Form.Item label={t('drawer.labels.roles')}>
            {rolesFetching ? (
              <Spin />
            ) : roles.length === 0 ? (
              <Text type="secondary">{t('drawer.noRoles')}</Text>
            ) : (
              <Space direction="vertical" size={6} style={{ width: '100%' }}>
                {roles.map((r) => (
                  <Checkbox
                    key={r.id}
                    checked={pending.has(r.name)}
                    onChange={(e) => {
                      const next = new Set(pending);
                      if (e.target.checked) next.add(r.name);
                      else next.delete(r.name);
                      setPending(next);
                    }}
                  >
                    <Space>
                      <Text strong>{r.name}</Text>
                      {r.isSystem ? <Tag color="blue">{t('drawer.system')}</Tag> : null}
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {t('drawer.permissionCount', { count: r.permissions.length })}
                      </Text>
                    </Space>
                  </Checkbox>
                ))}
              </Space>
            )}
          </Form.Item>

          <Space wrap>
            {!isEdit ? (
              <Button
                type="primary"
                htmlType="submit"
                loading={createMutation.isPending}
              >
                {t('drawer.buttons.create')}
              </Button>
            ) : (
              <>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={updateProfileMutation.isPending}
                >
                  {t('drawer.buttons.saveProfile')}
                </Button>
                <Button
                  loading={saveRolesMutation.isPending}
                  disabled={!dirtyRoles}
                  onClick={() => saveRolesMutation.mutate()}
                >
                  {t('drawer.buttons.saveRoles')}
                </Button>
              </>
            )}
          </Space>
        </Form>

        {isEdit ? (
          <>
            <div>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>
                {t('drawer.resetPassword.heading')}
              </Text>
              {resetPwd ? (
                <>
                  <Alert
                    type="success"
                    showIcon
                    message={t('drawer.resetPassword.oneTime')}
                    style={{ marginBottom: 8 }}
                  />
                  <Input.Password
                    value={resetPwd}
                    readOnly
                    visibilityToggle={{ visible: true }}
                    addonAfter={
                      <Button
                        type="link"
                        size="small"
                        onClick={() => {
                          void navigator.clipboard.writeText(resetPwd);
                          toast.success(t('drawer.toast.passwordCopied'));
                        }}
                      >
                        {t('drawer.buttons.copy')}
                      </Button>
                    }
                  />
                </>
              ) : (
                <Button
                  loading={resetPwdMutation.isPending}
                  onClick={() => resetPwdMutation.mutate()}
                >
                  {t('drawer.buttons.generatePassword')}
                </Button>
              )}
            </div>

            <UserProjectsSection
              userId={user?.id ?? ''}
              isSuperAdmin={isSuperAdmin(user?.roles)}
            />

            <div>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>
                {t('drawer.deleteUser.heading')}
              </Text>
              <Button
                danger
                loading={deleteMutation.isPending}
                onClick={() => {
                  if (
                    confirm(
                      t('drawer.deleteUser.confirm', { email: user?.email ?? '' }),
                    )
                  ) {
                    deleteMutation.mutate();
                  }
                }}
              >
                {t('drawer.deleteUser.button')}
              </Button>
            </div>
          </>
        ) : resetPwd ? (
          <Alert
            type="success"
            showIcon
            message={t('drawer.tempPasswordAlert.title')}
            description={
              <Input.Password
                value={resetPwd}
                readOnly
                visibilityToggle={{ visible: true }}
                addonAfter={
                  <Button
                    type="link"
                    size="small"
                    onClick={() => {
                      void navigator.clipboard.writeText(resetPwd);
                      toast.success(t('drawer.toast.passwordCopied'));
                    }}
                  >
                    {t('drawer.buttons.copy')}
                  </Button>
                }
              />
            }
          />
        ) : null}
      </Space>

      <div style={{ marginTop: 16 }}>
        <Button onClick={onClose}>{t('drawer.close')}</Button>
      </div>
    </Drawer>
  );
}

/**
 * Public wrapper that only renders the body when `open` is true. The body is
 * keyed by mode/user id so each (re)open starts from clean per-row state
 * without needing an effect-driven sync.
 */
export function AdminUserDrawer({
  open,
  mode,
  user,
  onClose,
  onSaved,
}: Props) {
  if (!open) return null;
  return (
    <AdminUserDrawerBody
      key={mode === 'edit' && user ? `edit:${user.id}` : 'create'}
      mode={mode}
      user={user}
      onClose={onClose}
      onSaved={onSaved}
    />
  );
}
